"use client";
import { Button } from "@/components/ui/button-pro";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { motion } from "framer-motion";
import {
  Clock,
  Globe,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
} from "lucide-react";
import type React from "react";
import { useState } from "react";

export function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    success?: boolean;
    message?: string;
  }>({});
  // const [captchaVerified, setCaptchaVerified] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // if (!captchaVerified) {
    //     setSubmitStatus({
    //         success: false,
    //         message: 'Please complete the captcha verification first.',
    //     });
    //     return;
    // }

    setIsSubmitting(true);
    setSubmitStatus({});

    try {
      const response = await fetch("/api/send-contact-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send message");
      }

      // setSubmitStatus({
      //     success: true,
      //     message:
      //         "Message sent successfully! I'll get back to you soon.",
      // });

      // Reset form
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending message:", error);
      setSubmitStatus({
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to send message. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      value: "hamza.missaoui28@gmail.com",
      href: "mailto:hamza.missaoui28@gmail.com",
      description: "Send me an email anytime",
    },
    {
      icon: Phone,
      title: "Phone",
      value: "+216 (56) 062-266",
      href: "tel:+21656062266",
      description: "Call me during business hours",
    },
    {
      icon: MapPin,
      title: "Location",
      value: "Tunisia, Tunis",
      href: "#",
      description: "Available for remote work worldwide",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  return (
    <section
      id="contact"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-purple-50 dark:from-black dark:via-gray-900 dark:to-purple-900/20"
    >
      <motion.div
        className="max-w-7xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <motion.div className="text-center mb-20" variants={itemVariants}>
          <h2 className="text-5xl sm:text-6xl font-bold mb-8">
            <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 dark:from-purple-400 dark:via-pink-400 dark:to-purple-400 bg-clip-text text-transparent">
              Get In Touch
            </span>
          </h2>
          <div className="w-32 h-1 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Ready to start your next project? Let's discuss how we can work
            together to bring your ideas to life.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div className="space-y-12" variants={itemVariants}>
            <div>
              <h3 className="text-3xl font-bold dark:text-white mb-6">
                Let's Connect
              </h3>
              <p className="text-lg dark:text-gray-400 leading-relaxed mb-8">
                I'm always interested in hearing about new opportunities and
                exciting projects. Whether you have a question or just want to
                say hi, feel free to reach out!
              </p>
            </div>

            <div className="space-y-8">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  className="flex items-start space-x-6 group"
                  whileHover={{ x: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center group-hover:from-purple-500/30 group-hover:to-pink-500/30 transition-all duration-300 shadow-lg">
                    <info.icon className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold dark:text-white text-gray-900 mb-2">
                      {info.title}
                    </h4>
                    <a
                      href={info.href}
                      className="text-lg dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors duration-300 block mb-2"
                    >
                      {info.value}
                    </a>
                    <p className="text-gray-600 dark:text-gray-500 text-sm">
                      {info.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Additional Info Cards */}
            <div className="grid sm:grid-cols-2 gap-6 mt-12">
              <motion.div
                className="bg-white/60 dark:bg-gray-800/60 p-6 rounded-2xl border border-purple-500/20 backdrop-blur-sm"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  <h4 className="font-bold text-gray-900 dark:text-white">
                    Response Time
                  </h4>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  Usually within 24 hours
                </p>
              </motion.div>

              <motion.div
                className="bg-white/60 dark:bg-gray-800/60 p-6 rounded-2xl border border-purple-500/20 backdrop-blur-sm"
                whileHover={{ y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  <h4 className="font-bold text-gray-900 dark:text-white">
                    Availability
                  </h4>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                  Available for new projects
                </p>
              </motion.div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="bg-white/80 dark:bg-gray-800/80 border-purple-500/20 backdrop-blur-sm shadow-2xl">
              <CardHeader className="pb-6">
                <div className="flex items-center gap-3 mb-2">
                  <MessageCircle className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                  <CardTitle className="text-3xl font-bold">
                    Send Message
                  </CardTitle>
                </div>
                <CardDescription className="text-lg text-gray-600 dark:text-gray-400">
                  Fill out the form below and I'll get back to you as soon as
                  possible.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Input
                        name="name"
                        placeholder="Your Name"
                        value={formData.name}
                        onChange={handleChange}
                        className="bg-white/50 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 focus:border-purple-500 dark:focus:border-purple-400 rounded-xl h-12 text-base"
                        required
                      />
                    </div>
                    <div>
                      <Input
                        name="email"
                        type="email"
                        placeholder="Your Email"
                        value={formData.email}
                        onChange={handleChange}
                        className="bg-white/50 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 focus:border-purple-500 dark:focus:border-purple-400 rounded-xl h-12 text-base"
                        required
                      />
                    </div>
                  </div>

                  <Input
                    name="subject"
                    placeholder="Subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="bg-white/50 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 focus:border-purple-500 dark:focus:border-purple-400 rounded-xl h-12 text-base"
                    required
                  />

                  <Textarea
                    name="message"
                    placeholder="Your Message"
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    className="bg-white/50 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 focus:border-purple-500 dark:focus:border-purple-400 rounded-xl resize-none text-base"
                    required
                  />

                  {/* <div className="space-y-3">
                                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Security Verification
                                        </label>
                                        <Captcha
                                            onVerify={setCaptchaVerified}
                                            className="bg-white/50 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600 rounded-xl p-4"
                                        />
                                    </div> */}

                  <Button
                    type="submit"
                    className="flex justify-center items-center bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-4 rounded-xl transition-all duration-300 transform hover:scale-105 gap-2 text-lg font-semibold shadow-lg hover:shadow-xl"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="h-6 w-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-6 h-6" />
                        Send Message
                      </>
                    )}
                  </Button>

                  {!submitStatus.success && submitStatus.message && (
                    <p className="text-red-500">{submitStatus.message}</p>
                  )}
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <motion.div
          className="mt-20 pt-8 border-t border-gray-200 dark:border-gray-800 text-center"
          variants={itemVariants}
        >
          <p className="text-gray-600 dark:text-gray-400">
            © 2025 Hamza Missaoui Portfolio. Built with Next.js and deployed on
            Vercel.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
}
